import clsx from 'clsx';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import useEventTracking from '@hooks/useEventTracking';
import useScrollToHash from '@hooks/useScrollToHash';
import { ClickTarget, click } from '@tracking';

import { EVENT_RATIO } from './constant';

const BECOME_A_PARTNER_TEXT = 'Become a partner';

const Glance = () => {
  const { trackEvent } = useEventTracking();
  const partnerRef = useScrollToHash({ hash: '#partner', offset: 100 });

  return (
    <article className="mx-auto max-w-[1728px]">
      <div className="bg-grayscale-charcoal px-16 sm:px-28">
        <div className="m-auto max-w-container-lg py-48 lg:pb-112 lg:pt-72">
          <h2 className="m-0 border-0 border-b border-solid border-white pb-32 font-suisse-screen text-headline-lg text-white">
            Events at a glance
          </h2>
          <div className="border-0 border-b border-solid border-white px-28 pb-48 pt-40 sm:px-56 md:px-72 lg:px-112 lg:pb-64">
            <div className="border-0 border-b border-solid border-white pb-64">
              <p className="pt-0 font-suisse-neue text-body-lg text-white sm:text-subheadline-md">
                In an ever-fragmented online world, real connections are rare and increasingly
                important. Some of the most significant conversations aren’t online, they’re
                face-to-face.
              </p>
              <p className="m-0 font-suisse-neue text-headline-xxs text-white">
                From intimate dinners to flagship summits, The Information’s events are designed for
                impact—ensuring every conversation provides a competitive edge and every interaction
                expands your orbit of influence.
              </p>
            </div>
            <h3
              className="m-0 pb-24 pt-64 text-headline-alt-sm text-white sm:text-headline-alt-md"
              ref={partnerRef}
              id="partner"
            >
              Why The Information?
            </h3>
            <p className="font-suisse-neue text-headline-xxs text-white">
              Built on a foundation of world-class journalism, we curate environments where the
              brightest minds in Silicon Valley—and beyond—gather to solve the industry’s most
              complex challenges.
            </p>
            <p className="font-suisse-neue text-headline-xxs text-white">
              When you attend an event by The Information, you aren’t just observing the news; you
              are part of the network that creates it.
            </p>
            <ul className="m-0 mt-24 grid list-none grid-cols-1 place-items-center p-0 md:grid-cols-3 md:place-items-baseline">
              {EVENT_RATIO.map((item, index) => (
                <li
                  key={item.ratio}
                  className={clsx(
                    'flex w-[250px] flex-col items-center text-balance px-6 py-16 text-left md:w-full md:min-w-full md:px-8 md:py-40 lg:px-10 lg:py-48',
                    {
                      'border-b border-l-0 border-r-0 border-t border-solid border-white/25 md:border-x md:border-b-0 md:border-t-0 md:border-solid md:border-white/25':
                        index === 1,
                    }
                  )}
                >
                  <div className="w-full max-w-[17rem] md:max-w-[14rem] lg:max-w-[16rem]">
                    <p className="m-0 mb-8 font-suisse-works text-headline-sm text-white md:mb-4 md:text-headline-xl">
                      {item.ratio}
                    </p>
                    <p className="m-0 font-suisse-intl text-overline-lg font-light text-white">
                      {item.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-72 flex flex-col items-center gap-24">
            <span className="text-center font-suisse-intl-mono text-caption-lg text-white sm:text-feed-headline-sm">
              Interested in sponsoring an event?
            </span>
            <PrimaryButton
              as="link"
              target="_blank"
              className="w-full sm:max-w-[200px]"
              onClickCapture={() =>
                trackEvent(
                  click({
                    clickTarget: ClickTarget.BecomePartner,
                    clickText: BECOME_A_PARTNER_TEXT,
                  })
                )
              }
            >
              {BECOME_A_PARTNER_TEXT}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </article>
  );
};

export default Glance;
